import { useCallback } from 'react'

import type { ApolloCache } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import {
  IntegrationsSyncRunOriginEnum,
  MarketplaceDbIntegration,
} from 'generated/resources/datatable.graphql'
import {
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunParameterFragment,
  IntegrationsSyncRunStatusChangeSubscriptionFragment,
  LastSyncRunDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { type Parameters } from 'modules/integrations/lib/parameters'
import { toSyncParameters } from 'modules/integrations/lib/parameters'

interface UpdateLastSyncRunQueryCacheProps {
  marketplaceIntegration: MarketplaceDbIntegration
  contextParameters: Parameters
  origin: IntegrationsSyncRunOriginEnum
  syncRun: IntegrationsSyncRunFragment | IntegrationsSyncRunStatusChangeSubscriptionFragment
}

interface ReadLastSyncRunProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  contextParameters: Parameters
  origin: IntegrationsSyncRunOriginEnum
  syncEnabled: boolean
  isBookkeeperView: boolean
}

interface SyncRunParameter {
  key: string
  value: string
}

interface LastSyncRunQueryVariables {
  marketplaceIntegrationId: string
  parameters: SyncRunParameter[]
  origin: IntegrationsSyncRunOriginEnum
}

export const buildLastSyncRunVariables = (
  marketplaceIntegrationId: string,
  contextParameters: Parameters,
  origin: IntegrationsSyncRunOriginEnum
): LastSyncRunQueryVariables => {
  return {
    marketplaceIntegrationId: marketplaceIntegrationId,
    parameters: toSyncParameters(contextParameters),
    origin,
  }
}

export const writeLastSyncRunQueryCache = (
  cache: ApolloCache<unknown>,
  { marketplaceIntegration, contextParameters, syncRun, origin }: UpdateLastSyncRunQueryCacheProps
) => {
  cache.writeQuery({
    query: LastSyncRunDocument,
    variables: buildLastSyncRunVariables(marketplaceIntegration.id, contextParameters, origin),
    data: {
      integrations: {
        syncRunsConnection: {
          nodes: [
            {
              id: syncRun.id,
              finishedAt: syncRun.finishedAt,
              status: syncRun.status,
              parameters: buildSyncRunParameters(syncRun.parameters),
              __typename: 'IntegrationsSyncRun',
            },
          ],
          __typename: 'IntegrationsSyncRunConnection',
        },
        __typename: 'integrations',
      },
    },
  })
}

const buildSyncRunParameters = (
  parameters: SyncRunParameter[]
): IntegrationsSyncRunParameterFragment[] => {
  return parameters.map((param) => ({
    key: param.key,
    value: param.value,
    __typename: 'IntegrationsSyncRunParameters',
  }))
}

export const useUpdateLastSyncRunQueryCache = () => {
  const client = useApolloClient()

  return useCallback(
    (props: UpdateLastSyncRunQueryCacheProps) => {
      writeLastSyncRunQueryCache(client.cache, props)
    },
    [client]
  )
}

export const useReadLastSyncRun = ({
  marketplaceIntegration,
  contextParameters,
  origin,
  syncEnabled,
  isBookkeeperView,
}: ReadLastSyncRunProps) => {
  return useQuery(LastSyncRunDocument, {
    variables: buildLastSyncRunVariables(
      marketplaceIntegration?.id ?? '0',
      contextParameters,
      origin
    ),
    skip: !syncEnabled,
    context: {
      isBookkeeperView,
    },
  })
}
